import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData, ScoreHeader, ScoreMetric } from '../data/types';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { colors, fontFamily } from '../data/designTokens';
import type { Severity } from '../data/designTokens';
import { RISK_GRADE_MODULE_TITLE } from '../constants';
import { getParameterImportance, IMPORTANCE_META } from '../data/riskFramework';

const fmt1 = (n: number | null | undefined): string => (n != null && Number.isFinite(n) ? n.toFixed(1) : '—');

// API returns scores on a 0–4 scale; we present them on a 0–10 scale where
// LOWER values mean a healthier credit profile (lower risk). Direction matches
// the API — no inversion needed.
const scaleToTen = (score4: number | null | undefined): number | null =>
    score4 == null || !Number.isFinite(score4) ? null : (score4 / 4) * 10;

// Grade buckets on the 0–10 scale. Low = best (A+), high = worst (D).
const gradeFromScore = (n: number | null | undefined): string => {
    if (n == null || !Number.isFinite(n)) return '—';
    if (n <= 1.5) return 'A+';
    if (n <= 3) return 'A';
    if (n <= 4.5) return 'B+';
    if (n <= 6) return 'B';
    if (n <= 8) return 'C';
    return 'D';
};

const toneForScore = (s: number | null): Severity => {
    if (s == null || !Number.isFinite(s)) return 'MEDIUM';
    if (s <= 3.5) return 'LOW';
    if (s <= 5) return 'MEDIUM';
    if (s <= 7) return 'HIGH';
    return 'SEVERE';
};

const toneLabel = (tone: Severity): string => {
    if (tone === 'LOW') return 'Low Risk';
    if (tone === 'MEDIUM') return 'Medium Risk';
    if (tone === 'HIGH') return 'Elevated Risk';
    return 'Severe Risk';
};

// ── Local severity palette (Score module only) ──────────────────────────
//
// Figma 131:85392 uses softer card surfaces than the shared `severity` map
// (which is tuned for inline chips / status pills, not large card fills).
// Per skill rule 7 the shared `severity` palette stays untouched — other
// modules (Alerts, Compliances, status chips) depend on its current values.
// These overrides are scoped to the Score module's cards + banner.
const SCORE_TONE: Record<Severity, { bg: string; border: string; ink: string; subdued: string }> = {
    LOW: { bg: '#D1FAE5', border: '#A7F3D0', ink: '#059669', subdued: '#047857' },
    MEDIUM: { bg: '#FFF7ED', border: '#FED7AA', ink: '#D97706', subdued: '#B45309' },
    HIGH: { bg: '#FEF2F2', border: '#FECACA', ink: '#DC2626', subdued: '#B91C1C' },
    SEVERE: { bg: '#FEE2E2', border: '#FCA5A5', ink: '#991B1B', subdued: '#7F1D1D' },
};

// Composite banner copy per tone — paired with the tone label as a single
// inline kicker line ("MEDIUM RISK · score 3.5–5. Monitor with…").
const TONE_BANNER_COPY: Record<Severity, string> = {
    LOW: 'score ≤ 3.5. Healthy zone — no immediate credit action.',
    MEDIUM: 'score 3.5–5. Monitor with standard due-diligence cadence.',
    HIGH: 'score 5–7. Material concerns — deeper diligence warranted.',
    SEVERE: 'score > 7. Severe risk — treat as red flag for credit decisioning.',
};

const scoredMetrics = (h: ScoreHeader): ScoreMetric[] => h.metrics.filter((m) => m.metricScore != null);
const unscoredMetrics = (h: ScoreHeader): ScoreMetric[] => h.metrics.filter((m) => m.metricScore == null);

const PILLAR_FALLBACK_DESCRIPTION: Record<string, string> = {
    'Revenue Score': 'Top-line strength — absolute scale of revenue and recent growth trajectory.',
    'Stability Score': 'Volatility in revenue and cash inflows across recent quarters.',
    'Customer Concentration Score': 'Dependence on the largest customers — risk if a few accounts dominate revenue.',
    'Counter Party Stability': 'Health of the company’s top customers and vendors based on their own credit signals.',
    'Counter Party Stability Score': 'Health of the company’s top customers and vendors based on their own credit signals.',
    'Liquidity Score': 'Short-term ability to meet obligations from current assets and cash on hand.',
    'Profitability Score': 'Operating margins and net profitability over the most recent reported period.',
    'Working Capital Score': 'Trade receivable / payable cycle and inventory holding efficiency.',
    'Compliance Score': 'Punctuality of statutory filings — GST returns, EPF and tax compliance.',
    'Solvency Score': 'Long-term debt servicing capacity relative to equity and cashflows.',
    'Leverage Score': 'Total debt and gearing relative to net worth and earnings.',
};

const pillarDescription = (h: ScoreHeader): string | undefined => h.tooltipDescription || PILLAR_FALLBACK_DESCRIPTION[h.displayName];

const COMPOSITE_DESCRIPTION =
    'AICA Risk Score is a composite credit grade on a 0–10 scale derived from filings, bureau data and behavioural signals. Lower = stronger credit profile / lower risk.';

const isMetricRedundantWithHeader = (h: ScoreHeader): boolean => {
    const scored = scoredMetrics(h);
    if (scored.length !== 1) return false;
    const m = scored[0];
    return (
        m.displayName.trim().toLowerCase() === h.displayName.trim().toLowerCase() &&
        h.headerScore != null &&
        Math.abs((m.metricScore ?? -999) - h.headerScore) < 0.05
    );
};

const weakestDrivers = (h: ScoreHeader, n = 3): ScoreMetric[] => {
    if (isMetricRedundantWithHeader(h)) return [];
    return scoredMetrics(h)
        .slice()
        .sort((a, b) => (b.metricScore ?? 0) - (a.metricScore ?? 0))
        .slice(0, n);
};

// Per-driver tone — colour the score on the right of each driver row based on
// that driver's own score, not the parent pillar's tone. Matches Figma: within
// the Financial card, "Debt / Equity 4.0" reads as green even though the
// pillar overall is amber.
const driverToneInk = (s: number | null): string => SCORE_TONE[toneForScore(s)].ink;

// ── Legend block ────────────────────────────────────────────────
const GRADE_LEGEND: Array<{ letter: string; range: string; meaning: string }> = [
    { letter: 'A+', range: '≤ 1.5', meaning: 'Strongest credit profile' },
    { letter: 'A', range: '1.51 – 3.00', meaning: 'Strong, low concern' },
    { letter: 'B+', range: '3.01 – 4.50', meaning: 'Solid, monitor periodically' },
    { letter: 'B', range: '4.51 – 6.00', meaning: 'Cautious, deeper diligence advised' },
    { letter: 'C', range: '6.01 – 8.00', meaning: 'At-risk, material concerns' },
    { letter: 'D', range: '> 8.00', meaning: 'Severe, treat as red flag' },
];

const ZONE_LEGEND: Array<{ tone: Severity; label: string; range: string; meaning: string }> = [
    { tone: 'LOW', label: 'Low Risk', range: 'score ≤ 3.5', meaning: 'No immediate action' },
    { tone: 'MEDIUM', label: 'Medium Risk', range: '3.5 – 5.0', meaning: 'Emerging risk; monitor' },
    { tone: 'HIGH', label: 'Elevated', range: '5.0 – 7.0', meaning: 'Material concerns' },
    { tone: 'SEVERE', label: 'Severe', range: '> 7.0', meaning: 'Red flag for credit' },
];

const IMPORTANCE_LEGEND_ORDER: Array<'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'> = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

// ---------------- Screen ----------------

const ScreenPillarCard: React.FC<{ header: ScoreHeader }> = ({ header }) => {
    const scaledHeader = scaleToTen(header.headerScore);
    const tone = toneForScore(scaledHeader);
    const sev = SCORE_TONE[tone];
    const drivers = weakestDrivers(header);
    const description = pillarDescription(header);
    return (
        <div
            style={{
                flex: 1,
                padding: 14,
                borderRadius: 6,
                background: sev.bg,
                border: `1px solid ${sev.border}`,
                minWidth: 240,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: colors.ink00, lineHeight: 1.3 }}>
                    {header.displayName}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'flex-end', flexShrink: 0 }}>
                    <span
                        style={{
                            fontSize: '1.3rem',
                            fontFamily: "'Geist Mono', monospace",
                            fontWeight: 600,
                            color: colors.ink00,
                            lineHeight: 1,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {fmt1(scaledHeader)}
                    </span>
                    <span
                        style={{
                            fontSize: '0.7rem',
                            color: colors.ink40,
                            marginLeft: 4,
                            fontFamily: "'Geist Mono', monospace",
                            lineHeight: 1,
                        }}
                    >
                        / 10
                    </span>
                </div>
            </div>
            <div
                style={{
                    fontSize: '0.65rem',
                    color: sev.ink,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    marginTop: 6,
                }}
            >
                {toneLabel(tone)}
            </div>
            {description ? (
                <div style={{ fontSize: '0.72rem', color: colors.ink40, marginTop: 8, lineHeight: 1.5 }}>{description}</div>
            ) : null}
            {drivers.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                    <div
                        style={{
                            fontSize: '0.62rem',
                            color: sev.ink,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginBottom: 4,
                        }}
                    >
                        Risk drivers
                    </div>
                    {drivers.map((m, i) => {
                        const importance = getParameterImportance(m.displayName);
                        const dScore = scaleToTen(m.metricScore);
                        return (
                            <div key={i}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '4px 0 5px',
                                        borderBottom: '1px solid #F0EFE9',
                                    }}
                                >
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: '0.78rem', color: colors.ink20, lineHeight: 1 }}>{m.displayName}</span>
                                        {importance ? (
                                            <span
                                                title={`Importance: ${IMPORTANCE_META[importance].label} (${IMPORTANCE_META[importance].weight}%)`}
                                                style={{
                                                    background: IMPORTANCE_META[importance].bg,
                                                    color: IMPORTANCE_META[importance].fg,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    letterSpacing: 0.3,
                                                    padding: '1px 4px',
                                                    borderRadius: 2,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {IMPORTANCE_META[importance].abbr}
                                            </span>
                                        ) : null}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '0.78rem',
                                            fontFamily: "'Geist Mono', monospace",
                                            color: driverToneInk(dScore),
                                            lineHeight: 1,
                                        }}
                                    >
                                        {fmt1(dScore)}
                                    </span>
                                </div>
                                {m.tooltipDescription ? (
                                    <div
                                        style={{ fontSize: '0.7rem', color: colors.ink40, lineHeight: 1.5, marginTop: 4, marginBottom: 4 }}
                                    >
                                        {m.tooltipDescription}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

const ScreenPendingSignals: React.FC<{ headers: ScoreHeader[] }> = ({ headers }) => {
    const groups = headers.map((h) => ({ name: h.displayName, pending: unscoredMetrics(h) })).filter((g) => g.pending.length > 0);
    if (groups.length === 0) return null;
    return (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.rule}` }}>
            <div
                style={{
                    fontSize: '0.7rem',
                    color: colors.ink40,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: 6,
                }}
            >
                Pending signals
            </div>
            {groups.map((g) => (
                <div key={g.name} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.75rem', color: colors.ink40, width: 130 }}>{g.name}</span>
                    {g.pending.map((m) => (
                        <ScreenChip key={m.displayName} tone="muted">
                            {m.displayName}
                        </ScreenChip>
                    ))}
                </div>
            ))}
        </div>
    );
};

const ScreenScoreLegend: React.FC = () => (
    <div style={{ marginTop: 20, paddingTop: 12, borderTop: `1px solid ${colors.rule}` }}>
        <div
            style={{
                fontSize: '0.7rem',
                color: colors.ink40,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
                marginBottom: 12,
            }}
        >
            How to read this score
        </div>
        <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 6 }}>Grade scale</div>
                {GRADE_LEGEND.map((g) => (
                    <div
                        key={g.letter}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '5px 0',
                            borderBottom: `0.5px solid ${colors.ruleSoft}`,
                            gap: 8,
                        }}
                    >
                        <span style={{ width: 32, fontSize: '0.85rem', fontWeight: 700, color: colors.ink00 }}>{g.letter}</span>
                        <span
                            style={{
                                width: 100,
                                fontSize: '0.7rem',
                                fontFamily: "'Geist Mono', monospace",
                                color: colors.ink20,
                            }}
                        >
                            {g.range}
                        </span>
                        <span style={{ flex: 1, fontSize: '0.72rem', color: colors.ink40, lineHeight: 1.45 }}>{g.meaning}</span>
                    </div>
                ))}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 6 }}>Severity zones</div>
                {ZONE_LEGEND.map((z) => {
                    const sev = SCORE_TONE[z.tone];
                    return (
                        <div
                            key={z.tone}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '5px 0',
                                borderBottom: `0.5px solid ${colors.ruleSoft}`,
                                gap: 8,
                            }}
                        >
                            <span
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 3,
                                    background: sev.bg,
                                    border: `1px solid ${sev.border}`,
                                    display: 'inline-block',
                                }}
                            />
                            <span style={{ width: 90, fontSize: '0.78rem', fontWeight: 700, color: sev.ink }}>{z.label}</span>
                            <span
                                style={{
                                    width: 90,
                                    fontSize: '0.7rem',
                                    fontFamily: "'Geist Mono', monospace",
                                    color: colors.ink20,
                                }}
                            >
                                {z.range}
                            </span>
                            <span style={{ flex: 1, fontSize: '0.72rem', color: colors.ink40, lineHeight: 1.45 }}>{z.meaning}</span>
                        </div>
                    );
                })}
            </div>
        </div>
        <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 2 }}>Importance tags</div>
            <div style={{ fontSize: '0.72rem', color: colors.ink40, marginBottom: 8, lineHeight: 1.5 }}>
                The small letter next to each driver inside a pillar card shows how heavily that parameter weighs in the composite score — a
                Critical parameter counts 4× a Low one.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                {IMPORTANCE_LEGEND_ORDER.map((k) => {
                    const meta = IMPORTANCE_META[k];
                    return (
                        <div key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span
                                style={{
                                    background: meta.bg,
                                    color: meta.fg,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    padding: '1px 4px',
                                    borderRadius: 2,
                                    lineHeight: 1,
                                }}
                            >
                                {meta.abbr}
                            </span>
                            <span style={{ fontSize: '0.72rem', color: colors.ink40, lineHeight: 1 }}>
                                {meta.label} ({meta.weight}%)
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
        <div style={{ fontSize: '0.72rem', color: colors.ink40, marginTop: 10, lineHeight: 1.55 }}>
            The composite score is the weighted average across pillars, then bucketed into the grade letter and severity zone above.
        </div>
        <div style={{ height: 1, background: colors.rule, marginTop: 14 }} />
    </div>
);

const ScreenScore: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const s = data.score;
    if (!s) return null;
    const scaledCompany = scaleToTen(s.companyScore);
    const grade = gradeFromScore(scaledCompany);
    const headlineTone = toneForScore(scaledCompany);
    const headlineSev = SCORE_TONE[headlineTone];
    return (
        <ScreenSection kicker="AICA grade" title={RISK_GRADE_MODULE_TITLE} index={index}>
            {/* Composite banner — soft beige surface, no purple. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 36,
                    padding: '18px 20px',
                    borderRadius: 6,
                    background: colors.surface,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ flexShrink: 0, minWidth: 140 }}>
                    <div
                        style={{
                            fontSize: '0.66rem',
                            color: colors.ink40,
                            fontWeight: 600,
                            letterSpacing: '0.09em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Composite Risk Score
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', marginTop: 8 }}>
                        <div
                            style={{
                                fontSize: '3.2rem',
                                color: colors.ink00,
                                fontFamily: "'Geist Mono', monospace",
                                fontWeight: 600,
                                lineHeight: 1,
                                letterSpacing: '-0.025em',
                            }}
                        >
                            {fmt1(scaledCompany)}
                        </div>
                        <div
                            style={{
                                fontSize: '0.85rem',
                                color: colors.ink40,
                                marginLeft: 6,
                                fontFamily: "'Geist Mono', monospace",
                                marginBottom: 4,
                            }}
                        >
                            / 10
                        </div>
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: 260, display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, minWidth: 64 }}>
                        <div
                            style={{
                                fontSize: '0.66rem',
                                color: colors.ink40,
                                letterSpacing: '0.09em',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                            }}
                        >
                            Grade
                        </div>
                        <div
                            style={{
                                fontSize: '2.4rem',
                                fontFamily: "'Fraunces', serif",
                                color: colors.ink00,
                                lineHeight: 1,
                                marginTop: 6,
                            }}
                        >
                            {grade}
                        </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: '0.75rem', lineHeight: 1.55 }}>
                            <span
                                style={{
                                    color: headlineSev.ink,
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {toneLabel(headlineTone)} ·{' '}
                            </span>
                            <span style={{ color: colors.ink40 }}>{TONE_BANNER_COPY[headlineTone]}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: colors.ink40, lineHeight: 1.55, marginTop: 10 }}>
                            {COMPOSITE_DESCRIPTION}
                        </div>
                    </div>
                </div>
            </div>
            {/* Pillar grid — 2-up rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
                {(() => {
                    const pairs: Array<[(typeof s.headers)[number] | undefined, (typeof s.headers)[number] | undefined]> = [];
                    for (let i = 0; i < s.headers.length; i += 2) pairs.push([s.headers[i], s.headers[i + 1]]);
                    return pairs.map((row, ri) => (
                        <div key={ri} style={{ display: 'flex', gap: 10 }}>
                            {row.map((h, ci) =>
                                h ? (
                                    <div key={ci} style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                        <ScreenPillarCard header={h} />
                                    </div>
                                ) : (
                                    <div key={ci} style={{ flex: 1 }} />
                                ),
                            )}
                        </div>
                    ));
                })()}
            </div>
            <ScreenScoreLegend />
        </ScreenSection>
    );
};

export const scoreModule: ModuleDefinition = {
    meta: {
        id: 'score',
        title: RISK_GRADE_MODULE_TITLE,
        description: 'Composite grade with per-pillar risk cards, weakest drivers and pending signals.',
        dataSource: 'Assessee Score API',
    },
    hasData: (d) => {
        if (!d.score || !Number.isFinite(d.score.companyScore)) return false;
        return d.score.companyScore > 0 || d.score.headers.some((h) => Number.isFinite(h.headerScore));
    },
    Screen: ScreenScore,
};

export { toneForScore };
